import React, { cloneElement, useState } from 'react';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type { HierarchyItem } from '@/shared/cors-provenance-api';

export interface HierarchyMenuSelection {
  item: HierarchyItem;
  index: number;
  /** Index 0: the root container file. */
  isContainer: boolean;
  /** Final entry: the cited extract. */
  isLast: boolean;
  /** Display name shown in the menu. */
  label: string;
}

/** A container's hierarchy is only worth a menu when it has more than one entry and is not stale. */
export function hasHierarchyMenu(
  hierarchy: HierarchyItem[] | null | undefined,
  loading: boolean,
): hierarchy is HierarchyItem[] {
  return !!hierarchy && hierarchy.length > 1 && !loading;
}

/** Extra row appended after the provenance hierarchy, with its own description. */
export interface HierarchyMenuTrailingItem {
  item: HierarchyItem;
  description: string;
}

const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown']);

function pathFileName(path: string): string {
  return path.split('?')[0].split('/').pop() ?? '';
}

function isMarkdownSignal(
  mimeType?: string | null,
  extension?: string | null,
  fileName?: string | null,
): boolean {
  const mime = (mimeType ?? '').toLowerCase();
  const ext = (extension ?? '').replace(/^\./, '').toLowerCase();
  return (
    mime.includes('markdown') ||
    MARKDOWN_EXTENSIONS.has(ext) ||
    /\.(md|markdown)$/i.test(fileName ?? '')
  );
}

/**
 * PDF preview of a cited markdown file, when provenance's ``previewUrl`` is a
 * distinct ``.pdf`` that is not already a hierarchy entry. Returns null for
 * missing, non-PDF, or non-markdown citations.
 */
export function markdownPdfPreviewItem(
  fileInfo: {
    previewUrl?: string | null;
    mimeType?: string | null;
    extension?: string | null;
    fileName?: string | null;
  } | null | undefined,
  hierarchy: HierarchyItem[],
): HierarchyItem | null {
  const previewUrl = fileInfo?.previewUrl?.trim() ?? '';
  if (!previewUrl) return null;
  if (hierarchy.some((entry) => entry.s3Url === previewUrl)) return null;

  const cited = hierarchy[hierarchy.length - 1];
  const citedIsMarkdown =
    isMarkdownSignal(fileInfo?.mimeType, fileInfo?.extension, fileInfo?.fileName) ||
    isMarkdownSignal(cited?.mimeType, null, cited?.fileName);
  if (!citedIsMarkdown) return null;

  const previewName = pathFileName(previewUrl);
  if (!/\.pdf$/i.test(previewName)) return null;

  return {
    kind: 'file',
    id: previewUrl,
    s3Url: previewUrl,
    fileName: previewName,
    mimeType: 'application/pdf',
  };
}

interface HierarchyActionMenuProps {
  hierarchy: HierarchyItem[];
  title: string;
  ariaLabel: string;
  onSelect: (selection: HierarchyMenuSelection) => void;
  /** Rows after the provenance hierarchy, such as a markdown file's PDF preview. */
  trailingItems?: HierarchyMenuTrailingItem[];
  /** The trigger button; its onClick and aria-haspopup are supplied here. */
  children: React.ReactElement;
}

/**
 * Dropdown of a container file's provenance hierarchy (root file, enclosing
 * subdocs, extracted target) for picking which file an action applies to.
 */
export function HierarchyActionMenu({
  hierarchy,
  title,
  ariaLabel,
  onSelect,
  trailingItems,
  children,
}: HierarchyActionMenuProps) {
  const t = useTimePilotT();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Menu
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      inverted
      placement="bottom-start"
      title={title}
      aria-label={ariaLabel}
      content={
        <>
          {hierarchy.map((item, index) => {
            const label =
              item.fileName || item.s3Url.split('/').pop() || t('sources.fileNumber', { index: index + 1 });
            return (
              <MenuItem
                key={`${item.s3Url}-${index}`}
                onSelect={() =>
                  onSelect({
                    item,
                    index,
                    label,
                    isContainer: index === 0,
                    isLast: index === hierarchy.length - 1,
                  })
                }
                description={index === 0 ? t('sources.sourceFile') : t('sources.extractedFile')}
              >
                {label}
              </MenuItem>
            );
          })}
          {trailingItems?.map(({ item, description }, index) => {
            const label =
              item.fileName ||
              item.s3Url.split('/').pop() ||
              t('sources.fileNumber', { index: hierarchy.length + index + 1 });
            return (
              <MenuItem
                key={`${item.s3Url}-trailing-${index}`}
                onSelect={() =>
                  onSelect({
                    item,
                    index: hierarchy.length + index,
                    label,
                    isContainer: false,
                    isLast: false,
                  })
                }
                description={description}
              >
                {label}
              </MenuItem>
            );
          })}
        </>
      }
    >
      {cloneElement(children, { 'aria-haspopup': 'menu', onClick: () => setIsOpen(!isOpen) })}
    </Menu>
  );
}
